/**
 * A short list paged like a carousel: PER_PAGE rows a page, sliding sideways. Arrows, a swipe (touch) or a
 * sideways scroll (trackpad, shift + wheel) move between pages; dots show where you are while the pages are few
 * (no count is shown: past that, the arrows alone). ON_END: called on reaching the last page, to load more (the list grows and paging carries on);
 * without it, the last page wraps round to the first.
 *
 *   <PagedList items={people} keyOf={(u) => u.id} renderItem={(u) => <Row … />} />
 */
import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Stack } from '@mui/material';
import { ChevronLeft as PrevIcon, ChevronRight as NextIcon } from '@mui/icons-material';

const MANY = 7;              // more pages than this (or a list that keeps loading): the arrows alone

export default function PagedList({ items, renderItem, keyOf = (x, i) => x?.id ?? i, perPage = 5, onEnd, spacing = 0.7, label = 'items' }) {
  const pages = Math.max(1, Math.ceil(items.length / perPage));
  const [page, setPage] = useState(0);
  const touch = useRef(null);
  const wheelLock = useRef(0);
  useEffect(() => { if (page >= pages) setPage(pages - 1); }, [page, pages]);
  // reaching the last page asks for more
  useEffect(() => { if (onEnd && page === pages - 1) onEnd(); }, [page, pages, onEnd]);

  const go = (dir) => setPage((p) => {
    const next = p + dir;
    if (next < 0) return onEnd ? 0 : pages - 1;
    if (next >= pages) return onEnd ? p : 0;
    return next;
  });
  const onWheel = (e) => {
    if (Math.abs(e.deltaX) < 20 || Math.abs(e.deltaX) < Math.abs(e.deltaY)) return;
    const now = Date.now();
    if (now - wheelLock.current < 450) return;          // one page per flick
    wheelLock.current = now;
    go(e.deltaX > 0 ? 1 : -1);
  };

  return (
    <Box onWheel={onWheel}
      onTouchStart={(e) => { touch.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        const dx = e.changedTouches[0].clientX - (touch.current ?? e.changedTouches[0].clientX);
        if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
        touch.current = null;
      }}>
      <Box sx={{ overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', transition: 'transform .35s ease', transform: `translateX(-${page * 100}%)` }}>
          {Array.from({ length: pages }, (_, i) => (
            <Stack key={i} spacing={spacing} sx={{ minWidth: '100%' }} aria-hidden={i !== page}>
              {items.slice(i * perPage, (i + 1) * perPage).map((item, j) => (
                <React.Fragment key={keyOf(item, i * perPage + j)}>{renderItem(item)}</React.Fragment>
              ))}
            </Stack>
          ))}
        </Box>
      </Box>
      {pages > 1 && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 1 }}>
          <IconButton size="small" onClick={() => go(-1)} aria-label={`Previous ${label}`} sx={{ p: 0.25 }}
            disabled={Boolean(onEnd) && page === 0}>
            <PrevIcon fontSize="small" />
          </IconButton>
          {pages > MANY || onEnd ? (
            <Box sx={{ width: 12 }} />
          ) : Array.from({ length: pages }, (_, i) => (
            <Box key={i} role="button" aria-label={`${label} page ${i + 1}`} onClick={() => setPage(i)}
              sx={{ width: i === page ? 16 : 6, height: 6, borderRadius: 3, cursor: 'pointer', transition: 'all .2s',
                bgcolor: i === page ? 'primary.main' : 'action.disabled' }} />
          ))}
          <IconButton size="small" onClick={() => go(1)} aria-label={`Next ${label}`} sx={{ p: 0.25 }}
            disabled={Boolean(onEnd) && page === pages - 1}>
            <NextIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Box>
  );
}
